import React from "react";
import { useRoutes, Navigate, useParams, type RouteObject } from "react-router-dom";
import { LayoutProvider } from "../theme/LayoutContext";
import SingLayout from "../sing/components/Layout/Layout";
import { UpdateCouple } from "../pages/Couple/UpdateCouple";
import MyProfile from "../pages/Admin/MyProfile";
import ParticipatedInRetreats from "../pages/Couple/ParticipatedInRetreats";
import { RegistrationRetreat } from "../pages/Auth/RegistrationRetreat";
import { getAuthUser } from "../hooks/useAuthUser";
import RetreatRoaster from "../pages/Admin/Retreats/RetreatRoster";
import ProjectedRetreatForm from "../pages/Admin/Retreats/ProjectedRetreatForm";
import ManageRetreat from "../pages/Admin/Retreats/ManageRetreat";
import RetreatSetupWorkspace from "../pages/Admin/Retreats/RetreatSetupWorkspace";
import CreateTicket from "../pages/TicketSystem/CreateTicket";
import TicketReply from "../pages/TicketSystem/TicketReply";
import UpcomingRetreats from "../pages/Auth/UpcomingRetreats";
import { RegisterWaitList } from "../pages/WaitList/RegisterWaitList";
import Directory from "../pages/Auth/Directory";
import Program from "../pages/Auth/Program";
import { CreateProgram } from "../pages/Admin/Retreats/CreateProgram";
import { CreateDirectory } from "../pages/Admin/Retreats/CreateDirectory";
import BuildMyTeam from "../pages/Admin/Retreats/BuildMyTeam";
import type { RouterWithLogoutProps } from "../types/layout";
import type { LoveStrongUser } from "../types/api";

type CoupleRouterProps = {
  logoutUser?: RouterWithLogoutProps["logoutUser"] | null;
};

function RedirectToRetreatSetup() {
  const { id } = useParams();
  return <Navigate to={`/retreat-setup/${id}`} replace />;
}

const CoupleRouter = ({ logoutUser = null }: CoupleRouterProps): React.ReactElement => {
  let userData = getAuthUser<LoveStrongUser>();

  const commonRotes: RouteObject[] = [
    { path: "/update-couple", element: <UpdateCouple /> },
    { path: "/my-profile", element: <MyProfile /> },
    { path: "/participated-in-retreats", element: <ParticipatedInRetreats /> },
    { path: "/registration-retreat/:id", element: <RegistrationRetreat /> },
    { path: "/registration-retreat", element: <RegistrationRetreat /> },
    { path: "/retreats/project", element: <ProjectedRetreatForm /> },
    { path: "/retreats/:id/project", element: <ProjectedRetreatForm /> },
    { path: "/register-new-retreat", element: <Navigate to="/retreats/project" replace /> },
    { path: "/create-ticket", element: <CreateTicket /> },
    { path: "/ticketReply/:id", element: <TicketReply /> },
    { path: "/upcoming-retreats", element: <UpcomingRetreats /> },
    { path: "/register-waitList/:id", element: <RegisterWaitList /> },
    {
      path: "/retreat-registrations",
      element: <Navigate to="/manageRetreat" replace />,
    },
  ];
  if (userData && userData.access === true) {
    commonRotes.push({
      path: "/retreatRoster",
      element: <RetreatRoaster />,
    });
    commonRotes.push({
      path: "/create-retreat/:id",
      element: <RedirectToRetreatSetup />,
    });
    commonRotes.push({
      path: "/directory/:id",
      element: <Directory />,
    });
    commonRotes.push({
      path: "/program/:id",
      element: <Program />,
    });
    commonRotes.push({
      path: "/create-program/:id",
      element: <CreateProgram />,
    });
    commonRotes.push({
      path: "/create-directory/:id",
      element: <CreateDirectory />,
    });
    commonRotes.push({
      path: "/manageRetreat",
      element: <ManageRetreat />,
    });
    commonRotes.push({
      path: "/retreat-setup/:id",
      element: <RetreatSetupWorkspace />,
    });
    commonRotes.push({
      path: "/build-my-team",
      element: <BuildMyTeam />,
    });
    commonRotes.push({
      path: "/new-retreats",
      element: <Navigate to="/manageRetreat" replace />,
    });
  }

  if (userData && userData.committeeMember === true) {
    commonRotes.push({
      path: "/manageRetreat",
      element: <ManageRetreat />,
    });
    commonRotes.push({
      path: "/retreat-setup/:id",
      element: <RetreatSetupWorkspace />,
    });
    commonRotes.push({
      path: "/retreatRoster",
      element: <RetreatRoaster />,
    });
    commonRotes.push({
      path: "/create-retreat",
      element: <Navigate to="/retreats/project" replace />,
    });
    commonRotes.push({
      path: "/create-retreat/:id",
      element: <RedirectToRetreatSetup />,
    });
    commonRotes.push({
      path: "/directory/:id",
      element: <Directory />,
    });
    commonRotes.push({
      path: "/program/:id",
      element: <Program />,
    });
    commonRotes.push({
      path: "/create-program/:id",
      element: <CreateProgram />,
    });
    commonRotes.push({
      path: "/create-directory/:id",
      element: <CreateDirectory />,
    });
    commonRotes.push({
      path: "/build-my-team",
      element: <BuildMyTeam />,
    });
  }

  if (userData && userData.coupleId === null) {
    commonRotes.push({
      path: "/manageRetreat",
      element: <ManageRetreat />,
    });
    commonRotes.push({
      path: "/*",
      element: <Navigate to="/manageRetreat" replace />,
    });
  } else {
    commonRotes.push({ path: "/*", element: <Navigate to="/update-couple" /> });
  }
  const route = useRoutes(commonRotes);

  commonRotes.push({
    path: "/directory/:id",
    element: <Directory />,
  });

  commonRotes.push({
    path: "/program/:id",
    element: <Program />,
  });

  let pathName: any = route;
  if (
    pathName.props.match.route.path === "/registration-retreat/:id" ||
    pathName.props.match.route.path === "/retreats/project" ||
    pathName.props.match.route.path === "/retreats/:id/project" ||
    pathName.props.match.route.path === "/create-ticket" ||
    pathName.props.match.route.path === "/ticketReply/:id" ||
    pathName.props.match.route.path === "/directory/:id" ||
    pathName.props.match.route.path === "/program/:id" ||
    pathName.props.match.route.path === "/register-waitList/:id"
  ) {
    return <>{route}</>;
  }
  return (
    <LayoutProvider>
      <SingLayout logoutUser={logoutUser}>{route}</SingLayout>
    </LayoutProvider>
  );
};

export default CoupleRouter;
